import React, { useEffect, useState } from 'react';
import { useHeader } from '../../context/HeaderContext';
import { createCampaignRequest } from '../../api/campaign';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MdCloudUpload, MdClose } from 'react-icons/md';

const CreateCampaignRequest = () => {
  const { setHeaderTitle } = useHeader();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    campaignType: '',
    landingPageURL: '',
    ctaButtonText: '',
    message: '',
    startDate: '',
    endDate: '',
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setHeaderTitle('Create Campaign Request');
  }, [setHeaderTitle]);

  const campaignTypes = [
    'Sponsor a Challenge',
    'Sponsor a Blog',
    'Promote Brand/Product',
    'Plant Trees',
    'Promote an Event',
    'Advertisement'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.campaignType) {
      newErrors.campaignType = 'Campaign type is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    } else if (formData.startDate && new Date(formData.endDate) <= new Date(formData.startDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      const requestData = {
        ...formData,
        campaign: formData.campaignType,
        description: formData.message,
        image: image || '',
      };
      await createCampaignRequest(requestData);
      toast.success('Campaign request created successfully!');
      setTimeout(() => {
        navigate('/campaigns/new-req');
      }, 1500);
    } catch (error) {
      console.error('Error creating campaign request:', error);
      toast.error('Failed to create campaign request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      campaignType: '',
      landingPageURL: '',
      ctaButtonText: '',
      message: '',
      startDate: '',
      endDate: '',
    });
    setImage(null);
    setImagePreview(null);
    setErrors({});
  };

  // Format date for display
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className='w-full'>
      <section className='bg-white rounded-md shadow-sm p-6'>
        <form onSubmit={handleSubmit}>
          <div className='flex gap-6'>
            {/* Left Column - Form Fields */}
            <div className='flex-1'>
              {/* Campaign Type */}
              <div className='mb-4'>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  <span className='text-red-500'>*</span> Campaign
                </label>
                <select
                  name='campaignType'
                  value={formData.campaignType}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${
                    errors.campaignType ? 'border-red-500' : 'border-gray-300'
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-primary`}
                >
                  <option value=''>Select Type of Campaign</option>
                  {campaignTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                {errors.campaignType && (
                  <p className='text-red-500 text-sm mt-1'>{errors.campaignType}</p>
                )}
              </div>

              {/* Landing Page URL */}
              <div className='mb-4'>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Landing Page URL
                </label>
                <input
                  type='url'
                  name='landingPageURL'
                  value={formData.landingPageURL}
                  onChange={handleInputChange}
                  placeholder='Landing Page URL eg. https://google.com'
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary'
                />
              </div>

              {/* CTA Button Text */}
              <div className='mb-4'>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  CTA Button Text
                </label>
                <input
                  type='text'
                  name='ctaButtonText'
                  value={formData.ctaButtonText}
                  onChange={handleInputChange}
                  placeholder='Enter CTA Button Text eg. View, Visit'
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary'
                />
              </div>

              {/* Message */}
              <div className='mb-4'>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Message
                </label>
                <textarea
                  name='message'
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder='Enter your message'
                  rows={4}
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none'
                />
              </div>

              {/* Date Row */}
              <div className='grid grid-cols-2 gap-4 mb-4'>
                {/* Campaign Start */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    <span className='text-red-500'>*</span> Campaign Start
                  </label>
                  <input
                    type='date'
                    name='startDate'
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${
                      errors.startDate ? 'border-red-500' : 'border-gray-300'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-primary`}
                  />
                  {errors.startDate && (
                    <p className='text-red-500 text-sm mt-1'>{errors.startDate}</p>
                  )}
                </div>

                {/* Campaign End */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    <span className='text-red-500'>*</span> Campaign End
                  </label>
                  <input
                    type='date'
                    name='endDate'
                    value={formData.endDate}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${
                      errors.endDate ? 'border-red-500' : 'border-gray-300'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-primary`}
                  />
                  {errors.endDate && (
                    <p className='text-red-500 text-sm mt-1'>{errors.endDate}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Image Upload */}
            <div className='w-64'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Image
              </label>
              <div className='border-2 border-dashed border-gray-300 rounded-lg p-4 text-center h-48 flex flex-col items-center justify-center'>
                {imagePreview ? (
                  <div className='relative w-full h-full'>
                    <img
                      src={imagePreview}
                      alt='Preview'
                      className='w-full h-full object-contain rounded'
                    />
                    <button
                      type='button'
                      onClick={removeImage}
                      className='absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600'
                    >
                      <MdClose size={16} />
                    </button>
                  </div>
                ) : (
                  <>
                    <MdCloudUpload className='text-4xl text-gray-400 mb-2' />
                    <p className='text-sm text-gray-500 mb-2'>Upload Media Here</p>
                    <label className='cursor-pointer'>
                      <span className='px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50'>
                        Choose File
                      </span>
                      <input
                        type='file'
                        accept='image/*'
                        onChange={handleImageChange}
                        className='hidden'
                      />
                    </label>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className='flex items-center justify-between mt-6 pt-4 border-t border-gray-200'>
            <button
              type='button'
              onClick={handleReset}
              className='px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium'
              disabled={loading}
            >
              Reset
            </button>
            <button
              type='submit'
              className='px-6 py-2 bg-primary text-white rounded-md hover:bg-primaryHoverDark transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed'
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Request'}
            </button>
          </div>
        </form>
      </section>

      <ToastContainer position="bottom-right" autoClose={2000} />
    </div>
  );
};

export default CreateCampaignRequest;
